/** Small scalar helpers shared by every pattern and by the mesh builder. */

export const TAU = Math.PI * 2;
export const DEG2RAD = Math.PI / 180;

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Maps `value` from `[inMin, inMax]` to `[outMin, outMax]` without clamping. */
export function remap(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number {
  const span = inMax - inMin;
  if (span === 0) return outMin;
  return outMin + ((value - inMin) / span) * (outMax - outMin);
}

/** Hermite ease between two edges, clamped to `0 .. 1`. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Quintic ease with zero first *and* second derivative at both ends. */
export function smootherstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Euclidean modulo: the result always carries the sign of `m`. */
export function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Distance from `n` to the nearest multiple of `m`, i.e. a triangle wave. */
export function triangleWave(n: number, m: number): number {
  const t = mod(n, m) / m;
  return t < 0.5 ? t * 2 : 2 - t * 2;
}

/**
 * Quantises `value` into `steps` bands. `steps <= 1` returns the value
 * unchanged so the control can be swept down to "continuous".
 */
export function quantize(value: number, steps: number): number {
  if (steps <= 1) return value;
  const n = Math.round(steps);
  return clamp01(Math.round(value * (n - 1)) / (n - 1));
}

/**
 * Symmetric S-curve around `0.5`. `amount` of `0` is a no-op, `1` is a hard
 * step. Used by the relief finish to sharpen or soften a whole design.
 */
export function contrastCurve(value: number, amount: number): number {
  const a = clamp01(amount);
  if (a === 0) return value;
  const eased = smootherstep(0, 1, clamp01(value));
  return lerp(value, eased, a);
}

/** Applies a gamma exponent while keeping the `0 .. 1` range. */
export function gammaCurve(value: number, gamma: number): number {
  if (gamma === 1) return value;
  return Math.pow(clamp01(value), Math.max(gamma, 1e-4));
}

/** Bilinear interpolation of the four corners of a unit cell. */
export function bilinear(
  c00: number,
  c10: number,
  c01: number,
  c11: number,
  tx: number,
  ty: number,
): number {
  return lerp(lerp(c00, c10, tx), lerp(c01, c11, tx), ty);
}
